import React, { useCallback, useEffect, useState } from 'react';
import { Scale, AlertTriangle, ChevronRight } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import CashMonthModal from './CashMonthModal';
import { formatCurrency, LoadingRow, errorText, UrduAmount } from './shared';

// Cash in hand at the start and end of every month with activity, oldest first, for finding the
// month that does not balance. No date filter on purpose: an opening balance is the sum of
// everything before it, so narrowing the range would change every figure on the page. A month
// opens its entries, with the cash in hand after each one.

const num = (v) => formatCurrency(v);

const ExpenseCashPosition = ({ department, cats, refreshKey, can, onEdit }) => {
  const showToast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // The open month by its key, so its figures come from the latest load after a save.
  const [openMonth, setOpenMonth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await expenseAPI.getCashPosition(department !== 'ALL' ? department : undefined));
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load the cash position'));
    } finally {
      setLoading(false);
    }
  }, [department, showToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const scopedName = department !== 'ALL' ? (cats.byId[department]?.name || 'this department') : 'all departments';
  const months = data?.months || [];
  const closing = Number(data?.closing || 0);
  const worst = data?.worst;
  const openIndex = openMonth ? months.findIndex((m) => m.month === openMonth) : -1;

  if (loading && !data) return <div className="bg-white rounded-2xl shadow-xl"><LoadingRow text="Working out the balances..." /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total withdrawn</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{num(data?.totalWithdrawn)}</p>
          <UrduAmount value={data?.totalWithdrawn ?? 0} className="text-blue-900/70" />
          <p className="text-[11px] text-gray-400 mt-0.5">Cash taken from the bank, {scopedName}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total spent</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{num(data?.totalSpent)}</p>
          <UrduAmount value={data?.totalSpent ?? 0} className="text-red-900/70" />
          <p className="text-[11px] text-gray-400 mt-0.5">Every expense, however it was paid</p>
        </div>
        <div className={`bg-white rounded-xl shadow-md p-4 border-l-4 ${closing < 0 ? 'border-red-500' : 'border-emerald-500'}`}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cash in hand now</p>
          <p className={`text-2xl font-bold mt-1 ${closing < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{num(closing)}</p>
          <UrduAmount value={closing} className={closing < 0 ? 'text-red-900/70' : 'text-emerald-900/70'} />
          <p className="text-[11px] text-gray-400 mt-0.5">Withdrawn less spent, all time</p>
        </div>
      </div>

      {worst && (
        <div role="alert" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">These figures do not balance.</p>
            <p className="mt-0.5">
              The running balance falls to <span className="font-semibold">{num(worst.closing)}</span> at the end of{' '}
              <span className="font-semibold">{worst.label}</span>. A cash box cannot hold less than nothing, so up to that point
              the ledger records more cash going out than it ever recorded coming in.
            </p>
            <p className="mt-1 text-xs text-red-800">
              Either some withdrawals were never entered, and the red months below show when, or cash reached this
              department another way, such as an opening float, which this ledger has no way to record.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Scale className="h-5 w-5 text-emerald-600" /> Month by month, {scopedName}
          </h3>
          <span className="text-xs text-gray-400">Oldest first · each month opens where the last one closed · click a month for its entries</span>
        </div>
        {months.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No entries yet, nothing to reconcile</div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Opening</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Withdrawn</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Spent</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Net</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Closing</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entries</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                  <th className="w-8" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {months.map((m) => {
                  const net = Number(m.net || 0);
                  const open = () => setOpenMonth(m.month);
                  return (
                    <tr
                      key={m.month}
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={onActivateKey(open)}
                      className={`border-b border-gray-50 cursor-pointer transition-colors ${m.negative ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'} ${ROW_FOCUS_RING}`}
                    >
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{m.label}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right tabular-nums whitespace-nowrap">{num(m.opening)}</td>
                      <td className="px-4 py-3 text-sm text-blue-700 font-semibold text-right tabular-nums whitespace-nowrap">{Number(m.withdrawn) ? `+ ${num(m.withdrawn)}` : '—'}</td>
                      <td className={`px-4 py-3 text-sm font-semibold text-right tabular-nums whitespace-nowrap ${m.overspent ? 'text-amber-700' : 'text-red-700'}`} title={`Cash ${num(m.cashSpent)} · cheque or transfer ${num(m.chequeSpent)}`}>
                        {Number(m.spent) ? `- ${num(m.spent)}` : '—'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right tabular-nums whitespace-nowrap ${net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {net >= 0 ? '+' : '-'} {num(Math.abs(net))}
                      </td>
                      <td className={`px-4 py-3 text-sm font-bold text-right tabular-nums whitespace-nowrap ${m.negative ? 'text-red-700' : 'text-gray-900'}`}>{num(m.closing)}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{m.withdrawalCount} in · {m.expenseCount} out</td>
                      <td className="px-4 py-3 text-xs">
                        {m.negative ? (
                          <span className="text-red-700 font-semibold">Short by {num(Math.abs(Number(m.closing)))}</span>
                        ) : m.overspent ? (
                          <span className="text-amber-700">Spent more than withdrawn, drew down the float</span>
                        ) : null}
                      </td>
                      <td className="pr-3 text-gray-300"><ChevronRight className="h-4 w-4" aria-hidden="true" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 leading-relaxed rounded-b-2xl">
              <span className="font-semibold text-amber-700">Amber</span>: the month spent more than it withdrew. Normal on its own,
              it ran down cash left over from before. <span className="font-semibold text-red-700">Red</span>: the balance has gone
              below zero, which real cash cannot do. That is where to look for a missing withdrawal. Hover a Spent figure to see how
              much was cash rather than cheque or transfer.
            </div>
          </>
        )}
      </div>

      {openIndex >= 0 && (
        <CashMonthModal
          month={months[openIndex]}
          previous={months[openIndex - 1] || null}
          next={months[openIndex + 1] || null}
          onStep={(m) => setOpenMonth(m.month)}
          scoped={department !== 'ALL'}
          scopedName={scopedName}
          departmentId={department !== 'ALL' ? department : undefined}
          refreshKey={refreshKey}
          canEdit={!!can && can('VIEW_ENTRIES_TAB')}
          onEdit={onEdit}
          onClose={() => setOpenMonth(null)}
        />
      )}
    </div>
  );
};

export default ExpenseCashPosition;
